/**
 * Policy Viewer - Scrollable policy document with section tracking
 *
 * Features:
 * - Scroll-to-read tracking
 * - Section-by-section acknowledgment
 * - Download PDF option
 * - Progress indicator
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Check, Download, ChevronDown, ChevronUp } from 'lucide-react';
import type { PolicyDocument, PolicySection } from '../../types/onboarding';
import { getPolicyAsText } from '../../lib/onboarding/policies';
import { cn } from '../../lib/utils';

interface PolicyViewerProps {
  policy: PolicyDocument;
  onAllSectionsAcknowledged: () => void;
  acknowledgedSections: string[];
  onSectionAcknowledge: (sectionId: string) => void;
}

export default function PolicyViewer({
  policy,
  onAllSectionsAcknowledged,
  acknowledgedSections,
  onSectionAcknowledge,
}: PolicyViewerProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(
    policy.sections[0]?.id || null
  );
  const [scrolledSections, setScrolledSections] = useState<Set<string>>(new Set());

  // Track scroll progress for each section
  const handleSectionScroll = useCallback(
    (sectionId: string, element: HTMLDivElement) => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Consider section "read" when scrolled past 90%
      if (scrollPercentage >= 0.9) {
        setScrolledSections((prev) => new Set(prev).add(sectionId));
      }
    },
    []
  );

  // Check if all required sections are acknowledged
  useEffect(() => {
    const requiredSections = policy.sections.filter((s) => s.required);
    const allAcknowledged = requiredSections.every((s) =>
      acknowledgedSections.includes(s.id)
    );

    if (allAcknowledged) {
      onAllSectionsAcknowledged();
    }
  }, [acknowledgedSections, policy.sections, onAllSectionsAcknowledged]);

  // Handle download
  const handleDownload = () => {
    const text = getPolicyAsText(policy);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${policy.id}-v${policy.version}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate progress
  const requiredCount = policy.sections.filter((s) => s.required).length;
  const acknowledgedCount = policy.sections.filter(
    (s) => s.required && acknowledgedSections.includes(s.id)
  ).length;
  const progressPercentage = (acknowledgedCount / requiredCount) * 100;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
      }}
    >
      {/* Header */}
      <div
        className="p-4 border-b"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              className="font-semibold text-lg"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {policy.title}
            </h3>
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Version {policy.version} | Last updated {policy.lastUpdated}
            </p>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1 text-xs">
            <span style={{ color: 'var(--color-text-muted)' }}>
              {acknowledgedCount} of {requiredCount} sections acknowledged
            </span>
            <span style={{ color: 'var(--color-accent)' }}>
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                backgroundColor: 'var(--color-accent)',
                width: `${progressPercentage}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
        {policy.sections.map((section) => (
          <PolicySectionItem
            key={section.id}
            section={section}
            isExpanded={expandedSection === section.id}
            isAcknowledged={acknowledgedSections.includes(section.id)}
            isScrolled={scrolledSections.has(section.id)}
            onToggle={() =>
              setExpandedSection(
                expandedSection === section.id ? null : section.id
              )
            }
            onAcknowledge={() => onSectionAcknowledge(section.id)}
            onScroll={(element) => handleSectionScroll(section.id, element)}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Section Item Component
// ============================================================================

interface PolicySectionItemProps {
  section: PolicySection;
  isExpanded: boolean;
  isAcknowledged: boolean;
  isScrolled: boolean;
  onToggle: () => void;
  onAcknowledge: () => void;
  onScroll: (element: HTMLDivElement) => void;
}

const PolicySectionItem = ({
  section,
  isExpanded,
  isAcknowledged,
  isScrolled,
  onToggle,
  onAcknowledge,
  onScroll,
}: PolicySectionItemProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (contentRef.current) {
      onScroll(contentRef.current);
    }
  };

  const canAcknowledge = isScrolled || isAcknowledged;

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: 'var(--color-border-subtle)' }}
    >
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-opacity-50"
        style={{
          backgroundColor: isAcknowledged
            ? 'rgba(34, 197, 94, 0.05)'
            : 'transparent',
        }}
      >
        <div className="flex items-center gap-3">
          {/* Acknowledgment Status */}
          <div
            className={cn(
              'w-5 h-5 rounded-full flex items-center justify-center',
              'transition-all duration-200'
            )}
            style={{
              backgroundColor: isAcknowledged
                ? 'var(--color-success)'
                : 'var(--color-bg-tertiary)',
              border: isAcknowledged
                ? 'none'
                : '1px solid var(--color-border)',
            }}
          >
            {isAcknowledged && (
              <Check className="w-3 h-3" style={{ color: 'white' }} />
            )}
          </div>

          <span
            className="font-medium text-sm"
            style={{
              color: isAcknowledged
                ? 'var(--color-success)'
                : 'var(--color-text-primary)',
            }}
          >
            {section.title}
          </span>

          {section.required && !isAcknowledged && (
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={{
                backgroundColor: 'var(--color-accent-muted)',
                color: 'var(--color-accent)',
              }}
            >
              Required
            </span>
          )}
        </div>

        {isExpanded ? (
          <ChevronUp
            className="w-5 h-5"
            style={{ color: 'var(--color-text-muted)' }}
          />
        ) : (
          <ChevronDown
            className="w-5 h-5"
            style={{ color: 'var(--color-text-muted)' }}
          />
        )}
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <div
            ref={contentRef}
            onScroll={handleScroll}
            className="max-h-64 overflow-y-auto p-4 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {section.content}
          </div>

          {/* Acknowledge Button */}
          {section.required && (
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={onAcknowledge}
                disabled={!canAcknowledge || isAcknowledged}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                style={{
                  backgroundColor: isAcknowledged
                    ? 'var(--color-success)'
                    : canAcknowledge
                    ? 'var(--color-accent)'
                    : 'var(--color-bg-tertiary)',
                  color: isAcknowledged || canAcknowledge
                    ? 'white'
                    : 'var(--color-text-muted)',
                }}
              >
                <Check className="w-4 h-4" />
                {isAcknowledged
                  ? 'Acknowledged'
                  : canAcknowledge
                  ? 'I have read and acknowledge this section'
                  : 'Please read to acknowledge'}
              </button>

              {!canAcknowledge && (
                <span
                  className="text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Scroll to the end to enable acknowledgment
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
